import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';

import Button from 'ui-lib/components/Button';
import FormikTextField from 'ui-lib/components/Inputs/TextField/FormikTextField';
import { useSnackbarContext } from 'ui-lib/components/Snackbar/SnackbarProvider';
import useRequest from 'web-shared/hooks/useRequest';
import { getRedirectPath } from 'web-shared/modules/auth/shared/utils';
import { SharedReduxState } from 'web-shared/reducers/types';
import { zitadelSSOService } from 'web-shared/services/sso-auth-service/vendors/zitadel';
import AuthLayout from 'web-shared/modules/auth/shared/components/AuthLayout';

import { getPublicCompanySSOConfig } from '../../../../../../frontend/global/modules/companies/api/sso-config';

import { AuthLocationState, signInFieldNameGenerator } from './const';
import AuthPageFormikWrapper from './components/AuthPageFormikWrapper';
import ForgotPasswordButton from './components/ForgotPasswordButton';
import SsoLoginButton from './components/SsoLoginButton';

interface Props {
  customLogo?: React.ReactNode;
}

const { n } = signInFieldNameGenerator;

export const AuthPage = ({ customLogo }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const authStore = useSelector((state: SharedReduxState) => state.auth);
  const [isAuthEnded, setIsAuthEnded] = useState(!!authStore.user);

  const location = useLocation<AuthLocationState | null>();
  const locationState = location.state;

  const { showSuccessSnackbar } = useSnackbarContext();
  const ssoConfigRequest = useRequest(getPublicCompanySSOConfig, undefined);

  useEffect(() => {
    if (ssoConfigRequest.data) {
      zitadelSSOService.setOrganizationId(ssoConfigRequest.data.zitadel_organization_id);
    }
  }, [ssoConfigRequest.data]);

  useEffect(() => {
    if (!locationState?.showPasswordResetSuccess) return;

    showSuccessSnackbar({
      title: t('auth.title_passwordSaved'),
      message: t('auth.description_passwordChangedSuccessfully'),
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [
    location.pathname,
    locationState?.showPasswordResetSuccess,
    navigate,
    showSuccessSnackbar,
    t,
  ]);

  if (isAuthEnded) return <Navigate to={locationState?.from && !authStore.signedOut ? getRedirectPath(locationState.from) : '/'} replace />;

  return (
    <AuthPageFormikWrapper onAuthEnd={() => setIsAuthEnded(true)}>
      {({ handleSubmit, isValid, isSubmitting }) => (
        <AuthLayout
          customLogo={customLogo}
          loading={ssoConfigRequest.loading}
          onSubmit={handleSubmit}
          title={t('auth.title_login')}
        >
          {({ companySubdomain }) => {
            const ssoConfig = ssoConfigRequest.data;
            const showCredentialsLogin = !ssoConfig?.enabled || !ssoConfig?.enforced || !companySubdomain;
            const hasSSOLogin = !!(ssoConfig?.enabled && companySubdomain);

            return (
              <>
                {showCredentialsLogin && (
                  <Stack>
                    <FormikTextField
                      label={t('auth.label_email')}
                      type='email'
                      name={n('email')}
                      disabled={isSubmitting}
                      required
                      testID='email'
                    />
                    <FormikTextField
                      label={t('auth.label_password')}
                      type='password'
                      name={n('password')}
                      disabled={isSubmitting}
                      required
                      testID='password'
                    />
                    <ForgotPasswordButton />
                  </Stack>
                )}
                <Stack spacing={1.5}>
                  {showCredentialsLogin && (
                    <Button
                      fullWidth
                      type='submit'
                      variant='contained'
                      size='large'
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting}
                      testID='login-button'
                    >
                      {t('auth.label_login')}
                    </Button>
                  )}
                  {hasSSOLogin && (
                    <SsoLoginButton
                      disabled={isSubmitting}
                      config={ssoConfig}
                      ssoService={zitadelSSOService}
                      subdomain={companySubdomain ?? ''}
                      testID='zitadel'
                    />
                  )}
                </Stack>
              </>
            );
          }}
        </AuthLayout>
      )}
    </AuthPageFormikWrapper>
  );
};

export default AuthPage;
